import { TopbarCustomSlot } from '@cw-elements/host-kit';
import type { TopbarNavigationMFEProps } from '@cw-elements/mfe-setup/util';
import { Flex } from 'antd';
import { Suspense, useMemo } from 'react';

import { ENV_VARIABLES } from '@/constants';
import { useFeatureFlags } from '@/contexts';
import { lazyWithRetry } from '@/utils';

import ErrorBoundary from '../ErrorBoundary';

import { SelectSeason, TopbarFallback } from './components';
import { PANORAMA_APP_NAME, TOPBAR_ERROR_CONTEXT } from './constants';

const TopbarNavigation = lazyWithRetry(() =>
  import('@cw-elements/mfe-setup/load').then(({ loadRemoteModule }) =>
    loadRemoteModule<TopbarNavigationMFEProps>('topbar-navigation/index')
  )
);

type TopbarProps = {
  hideRegionSeasonSelector?: boolean;
};

const Topbar = ({ hideRegionSeasonSelector = false }: TopbarProps) => {
  const { isEnabled } = useFeatureFlags();
  const isSearchTreePopoverEnabled = isEnabled(
    'releaseWebTopbarSearchTreeSelectors'
  );
  const selectorGap = isSearchTreePopoverEnabled ? 4 : 20;
  const navigationProps = useMemo(
    () => ({
      onLogout: () => {},
      productImage: {
        redirectUrl: '/',
        imageSrc: '/cropwise-planting-logo.svg',
        name: PANORAMA_APP_NAME
      },
      appData: {
        id: ENV_VARIABLES.PANORAMA_APP_ID,
        name: PANORAMA_APP_NAME
      },
      featureConfig: {
        hasSettingsMenu: true,
        hasProfileDropdown: true,
        hasDeckOfApps: true,
        hideEntitiesDropdown: true
      },
      profileDropdownConfig: () => ({
        availableLanguages: ['en'],
        availableUnitSystems: [],
        onLanguageChange: () => {}
      })
    }),
    []
  );

  return (
    <ErrorBoundary
      errorContext={TOPBAR_ERROR_CONTEXT}
      fallback={
        <TopbarFallback
          hideRegionSeasonSelector={hideRegionSeasonSelector}
          selectorGap={selectorGap}
        />
      }>
      <Suspense
        fallback={
          <TopbarFallback
            hideRegionSeasonSelector={hideRegionSeasonSelector}
            selectorGap={selectorGap}
          />
        }>
        <TopbarNavigation userLanguage='en' {...navigationProps} />
        {!hideRegionSeasonSelector && (
          <TopbarCustomSlot>
            <Flex gap={selectorGap}>
              <SelectSeason />
            </Flex>
          </TopbarCustomSlot>
        )}
      </Suspense>
    </ErrorBoundary>
  );
};

export default Topbar;
